<template>
  <div class="expense-sheet-item-card-list">
    <Card
      v-for="item in items"
      :key="item.id"
      class="expense-item-card"
    >
      <template #content>
        <h3 class="item-title">{{ item.expenseDescription }}</h3>
        
        <div class="item-row">
          <div class="item-field">
            <small class="field-label">{{ t('wkt.client.expensesheets.detail-window.items.ticket_date') }}</small>
            <span class="field-value">{{ formatLongDateTime(item.ticketDate) }}</span>
          </div>
        </div>
        
        <div class="item-row two-columns">
          <div class="item-field">
            <small class="field-label">{{ t('wkt.client.expensesheets.detail-window.items.category') }}</small>
            <span class="field-value">{{ item.categoryName }}</span>
          </div>
          <div class="item-field">
            <small class="field-label">{{ t('wkt.client.expensesheets.detail-window.items.format_type') }}</small>
            <span class="field-value">{{ getFormatTypeText(Number(item.formatType)) }}</span>
          </div>
        </div>
        
        <div class="item-row two-columns">
          <div class="item-field">
            <small class="field-label">{{ t('wkt.client.expensesheets.detail-window.items.amount') }}</small>
            <span class="field-value amount">{{ formatCurrency(item.totalAmount) }}</span>
          </div>
          <div class="item-field">
            <small class="field-label">{{ t('wkt.client.expensesheets.detail-window.items.payment_method') }}</small>
            <span class="field-value">{{ getPaymentMethodText(Number(item.paymentMethod)) }}</span>
          </div>
        </div>
        
        <div v-if="item.documentPath" class="item-row">
          <a class="document-link" @click.stop="$emit('download-document', item)">
            {{ item.documentName }}
          </a>
        </div>
      </template>
      
      <template #footer>
        <div class="card-footer">
          <div class="spacer"></div>
          <Button
            v-if="[-1, 2].includes(Number(parent.status))"
            :label="t('wkt.client.expensesheets.detail-window.items.button-edit')"
            class="footer-primary-button"
            color="#185EA9"
            variant="text"
            @click.stop="$emit('edit-item', item)"
          />
          <Button
            v-else
            :label="t('wkt.client.expensesheets.detail-window.items.button-view')"
            class="footer-primary-button"
            color="#185EA9"
            variant="text"
            @click.stop="$emit('view-item', item)"
          />
        </div>
      </template>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useDateFormatter } from '@wkt/composables/useDateFormatter'
import { useExpenseSheets } from '@wkt/composables/useExpenseSheets'
import { useUtils } from '@wkt/composables/useUtils'

// Props
interface Props {
  parent: any
  items: any[]
}

defineProps<Props>()

// Emits
defineEmits<{
  'download-document': [item: any]
  'edit-item': [item: any]
  'view-item': [item: any]
}>()

// Composables
const { t } = useI18n()
const { formatLongDateTime } = useDateFormatter()
const { getFormatTypeText, getPaymentMethodText } = useExpenseSheets()
const { formatCurrency } = useUtils()
</script>

<style scoped>
.expense-sheet-item-card-list {
  padding-top: 0;
}

.expense-item-card {
  margin-top: 1rem;
  border: 1px solid var(--border-color);
  box-shadow: none;
}

.expense-item-card :deep(.p-card-body) {
  padding: 0;
}

.expense-item-card :deep(.p-card-content) {
  padding: 1rem;
}

.expense-item-card :deep(.p-card-footer) {
  padding: 0.5rem 1rem;
  border-top: 1px solid var(--border-color);
}

.item-title {
  color: #000;
  font-size: 1.2em;
  line-height: 24px;
  margin: 0 0 0.75rem 0;
  font-weight: bold;
}

.item-row {
  margin-bottom: 0.75rem;
}

.item-row:last-child {
  margin-bottom: 0;
}

.item-row.two-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.item-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.field-label {
  color: #424242;
  font-size: 14px;
  display: block;
}

.field-value {
  color: #000;
  font-size: 1.3em;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.field-value.amount {
  color: var(--red-500);
}

.document-link {
  color: var(--primary-color);
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.document-link:hover {
  text-decoration: none;
}

.card-footer {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.spacer {
  flex: 1;
}
</style>